import * as React from 'react';
import { reduxForm, Field, getFormSyncErrors } from 'redux-form';
const { connect } = require('react-redux');
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
import { ErrorSummary, Modal, Button } from 'components';
import * as c from 'models/constants';
import { BookingInputField, BookingSelectMenu } from 'components/FormControls';
import { requestFailure } from 'redux/modules/image';
import { required } from 'helpers/validationattributes';
import { Dispatch } from 'react-redux';
import { closeModal } from 'redux/modules/modal';
import { IServiceGroup, IServiceGroupColor, IServiceGroupsForm, IServiceTree } from 'models/shop/serviceGroups';
import {
	addServiceGroup,
	keyToServiceCategory,
	saveOfficeServiceGroups,
	updateServiceGroup,
} from 'redux/modules/shop/serviceGroups/helpers';
import { createDomainValueOptions } from 'helpers/common';
import renderIcon from 'helpers/renderIcon';
import { IServiceCategoryDomainValue } from 'models/common';

interface IOwnProps {
	officeId: string;
	servicesFormData: Partial<IServiceGroupsForm>;
	serviceCategories: IServiceCategoryDomainValue[];
	serviceCategory?: any;
	serviceGroupData?: IServiceTree & IServiceGroup;
	shopTypes: number[];
	mode: 'add' | 'edit';

	// function props
	t: any;
	afterAddingEditingGroup: () => any;
}

interface IProps extends IOwnProps, ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps> {}

interface IState {
	color: IServiceGroupColor;
}

const fieldNames: { [key in keyof IServiceGroup | any]: key } = {
	value: 'value',
	serviceCategoryId: 'serviceCategoryId',
};

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class AddEditServiceGroupForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			color: '#EA3546', // Start with first available color
		};

		this.handleSubmit = this.handleSubmit.bind(this);
	}

	public componentDidMount() {
		if (this.props.mode === 'edit' && this.props?.serviceGroupData && this.props?.serviceGroupData?.color !== null) {
			this.setState({ color: this.props.serviceGroupData.color });
		}
	}

	public handleSubmit() {
		const { initialValues } = this.props;
		const formData: Partial<IServiceGroup> = this.props.currentFormValues;
		const categories = this.props.servicesFormData.serviceCategoryTree.map((sct) => sct[0].serviceCategoryId); // Find service category index in services form
		const index = categories?.indexOf(formData.serviceCategoryId);
		const tempServiceGroups = this.props.servicesFormData.serviceCategoryTree[index];

		if (this.props.mode === 'add') {
			let order = null;

			// Check if the service category already contains any groups and add it to the last place else give it order 1
			if (this.props.servicesFormData.serviceCategoryTree[index]) {
				order = tempServiceGroups[tempServiceGroups?.length - 1]?.order + 1 || (index + 1) * 1000 + 1;
			} else {
				// If no service categories exists assign first order number
				order = (index + 1) * 1000 + 1;
			}

			const newServiceGroupData: IServiceGroup = {
				value: formData.value,
				serviceCategoryId: formData.serviceCategoryId,
				order: order,
				officeId: this.props.officeId,
				parentId: null,
				id: null,
				color: this.state.color,
			};

			this.props
				.addServiceGroup(() => this.props.state, newServiceGroupData)
				.then(() => {
					this.props.afterAddingEditingGroup();
					this.props.closeModal();
				})
				.catch((e) => {
					console.error('Adding service group failed', e);
				});
		} else if (this.props.mode === 'edit') {
			const { serviceGroups } = this.props;
			const childrenServiceGroups: IServiceGroup[] = (this.props.serviceGroupData.children as IServiceGroup[]) || [];
			const childServiceGroupsToUpdate: IServiceGroup[] = [];

			// Don't make call if everything is the same
			if (
				JSON.stringify(initialValues) === JSON.stringify(formData) &&
				this.props.serviceGroupData.color === this.state.color
			) {
				this.props.closeModal();
				return;
			}

			// Get children service groups to update (if color or service category changed then update child service groups aswell)
			if (childrenServiceGroups?.length) {
				childrenServiceGroups.forEach((ch) => {
					const tempServiceGroup = serviceGroups.find((sg) => sg.id === ch.id);
					if (tempServiceGroup) {
						childServiceGroupsToUpdate.push(tempServiceGroup);
					}
				});
			}

			// Change order if not the same service category anymore
			let newOrder = this.props.serviceGroupData.order;

			if (this.props.serviceGroupData.serviceCategoryId !== formData.serviceCategoryId) {
				// Check if existing service category and add it to the end
				if (this.props.servicesFormData.serviceCategoryTree[index]) {
					newOrder = tempServiceGroups[tempServiceGroups?.length - 1]?.order + 1 || (index + 1) * 1000 + 1;
				} else {
					// If service category doesn't yet exist get length of service categories and add 1
					newOrder = (categories?.length + 1) * 1000 + 1;
				}

				// Update children aswell
				if (childServiceGroupsToUpdate.length) {
					childServiceGroupsToUpdate.forEach((csg) => {
						csg.serviceCategoryId = formData.serviceCategoryId;
					});
				}
			}

			// Update children's color value
			if (this.props.serviceGroupData.color !== this.state.color && childServiceGroupsToUpdate.length) {
				childServiceGroupsToUpdate.forEach((csg) => {
					csg.color = this.state.color;
				});
			}

			// Create a variable that requires only the necessary data for the call (parent service group)
			const updatedServiceGroupData: IServiceGroup = {
				id: this.props.serviceGroupData.id,
				value: formData.value,
				parentId: this.props.serviceGroupData.parentId,
				serviceCategoryId: formData.serviceCategoryId,
				officeId: this.props.serviceGroupData.officeId,
				order: newOrder,
				color: this.state.color,
			};

			const serviceGroupsToUpdate: IServiceGroup[] = [updatedServiceGroupData, ...childServiceGroupsToUpdate];

			this.props
				.saveOfficeServiceGroups(() => this.props.state, this.props.serviceGroupData.officeId, serviceGroupsToUpdate)
				.then(() => {
					this.props.afterAddingEditingGroup();
					this.props.closeModal();
				})
				.catch((err) => {
					console.error('Updating service group failed', err);
				});
		}
	}

	private mapErrorKeyToMessage(key: string) {
		switch (key) {
			default:
				return key;
		}
	}

	get validServiceCategories() {
		const { serviceCategories, shopTypes, currentFormValues } = this.props;
		if (!serviceCategories?.length || !Array.isArray(shopTypes)) {
			return null;
		}
		const alreadyUsed = Object.keys(currentFormValues).map(keyToServiceCategory).filter(Boolean);
		// intersect
		return serviceCategories.filter(
			(sc) => alreadyUsed.includes(sc.key) || shopTypes.some((st) => sc.serviceCategoryShopTypes.includes(st)),
		);
	}

	get selectedServiceCategory() {
		return this.props.serviceCategory || this.validServiceCategories?.[0]?.key;
	}

	private options = createDomainValueOptions(this.props.t, 'DV_ServiceCategories_', this.validServiceCategories, false);
	private colors: IServiceGroupColor[] = [
		'#EA3546',
		'#FF5722',
		'#FF9800',
		'#FFC107',
		'#FFEB3B',
		'#CDDC39',
		'#8BC34A',
		'#66BB6A',
		'#26A69A',
		'#00BCD4',
		'#40C4FF',
		'#448AFF',
		'#536DFE',
		'#7C4DFF',
		'#C407E4',
		'#FF4081',
	];

	public render() {
		const { formErrors, officeId, currentFormValues, t, mode } = this.props;

		const errorSummaryProps = {
			formErrors,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		let allDataPresent = true;

		if (!officeId || Object.keys(currentFormValues).length < 2) {
			allDataPresent = false;
		}

		const isServiceCategoryDropdownDisabled: boolean = mode === 'edit' ? true : false;

		return (
			<Modal
				modalClass="modal-with-form add-edit-serviceGroup-modal serviceGroupModal"
				closeModal={this.props.closeModal}
				size="small"
				showHeaderCloseButton={false}
			>
				<form autoComplete="off">
					<div className="container">
						<div className="row">
							<div className="col-12">
								<div className="serviceGroupModal__titleContainer">
									<h1 className="serviceGroupModal__title">
										{this.props.mode === 'add'
											? t('User_ShopCard_Services_NewServiceGroup')
											: t('User_ShopCard_Services_EditServiceGroup')}
									</h1>
									<div className="serviceGroupModal__closeButton" onClick={this.props.closeModal}>
										{renderIcon('cancel', 20, 20)}
									</div>
								</div>
							</div>
						</div>

						<div className="row">
							<div className="col-12">
								<Field
									className="serviceGroupModal__nameField"
									name={fieldNames.value}
									component={BookingInputField}
									type="text"
									label={t('User_ShopCard_Services_Group_Value_Label')}
									information={t('User_ShopCard_Services_Group_Value_Label')}
									validate={[required]}
									placeholder={t('User_ShopCard_Example_Hair_Services')}
								/>
							</div>
							<div className="col-12">
								<div className="serviceGroupModal__serviceCategoriesField">
									<Field
										name={fieldNames.serviceCategoryId}
										component={BookingSelectMenu}
										type="text"
										items={this.options}
										multi={false}
										validate={[required]}
										isRequired={true}
										label={t('User_ShopCard_Services_ServiceCategory')}
										information={t('User_ShopCard_Services_ServiceCategory')}
										isDisabled={isServiceCategoryDropdownDisabled} // Disable for now in edit mode as reordering causes child booking templates to not update their Attribute's serviceCategoryId value
										searchable={false}
									/>
								</div>
							</div>

							<div className="col-12">
								<div className="serviceGroupModal__colorsTitle">{t('User_ShopCard_Assign_Colour')}</div>
								<div className="serviceGroupModal__colorsContainer">
									{this.colors.map((color) => {
										return (
											<div
												className="serviceGroupModal__colorsContainer__color"
												key={color}
												style={{ backgroundColor: color }}
												onClick={() => this.setState({ color: color })}
											>
												{this.state.color === color ? (
													<div
														className="serviceGroupModal__colorsContainer__colorActive"
														style={{ outline: `2px solid ${this.state.color !== color ? color : '#FFFFFF'}` }}
													>
														{renderIcon('check')}
													</div>
												) : null}
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</div>

					<div className="serviceGroupModal__bottomContainer">
						<div className="container">
							<div className="row">
								<div className="col-12">
									<div className="serviceGroupModal__bottomContainer__buttons">
										<div className="serviceGroupModal__cancelButtonMobile" onClick={this.props.closeModal}>
											{t('BSPortal_Common_Cancel')}
										</div>
										<Button
											disabled={!allDataPresent}
											className="serviceGroupModal__createButton"
											onClick={this.handleSubmit}
										>
											{this.props.mode === 'add' ? t('BSPortal_Common_Create') : t('BSPortal_Common_Save')}
										</Button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</form>
				<div>
					<ErrorSummary {...errorSummaryProps} />
				</div>
			</Modal>
		);
	}
}

function mapStateToProps(state: IStore, _ownProps: IOwnProps) {
	let currentFormValues: Partial<IServiceGroup> = {};

	if (state.form[c.FORM_NAME_ADD_EDIT_SERVICE_GROUP] && state.form[c.FORM_NAME_ADD_EDIT_SERVICE_GROUP].values) {
		currentFormValues = state.form[c.FORM_NAME_ADD_EDIT_SERVICE_GROUP].values;
	}

	// Used when updating
	let initialValues: Partial<IServiceGroup> = {};
	if (_ownProps.mode === 'edit') {
		initialValues = {
			value: _ownProps?.serviceGroupData.value || '',
			serviceCategoryId: _ownProps?.serviceGroupData.serviceCategoryId || null,
			color: _ownProps.serviceGroupData.color,
		};
	}
	return {
		manageShop: state.manageShop,
		state,
		currentFormValues,
		initialValues,
		serviceGroups: state.serviceGroups.serviceGroups,
		formErrors: getFormSyncErrors(c.FORM_NAME_ADD_EDIT_SERVICE_GROUP)(state),
	};
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mapDispatchToProps = (dispatch: Dispatch<IStore>, _ownProps: IOwnProps) => ({
	addServiceGroup: (getState: () => IStore, serviceGroup: IServiceGroup) => addServiceGroup(getState, serviceGroup),
	updateServiceGroup: (getState: () => IStore, serviceGroupId: string, serviceGroup: IServiceGroup) =>
		updateServiceGroup(getState, serviceGroupId, serviceGroup), // If different service group then update children aswell
	saveOfficeServiceGroups: (getState: () => IStore, officeId: string, serviceGroupsToUpdate: IServiceGroup[]) =>
		saveOfficeServiceGroups(getState, officeId, [], serviceGroupsToUpdate, []),
	requestFailure: (error) => dispatch(requestFailure(error)),
	closeModal: () => dispatch(closeModal()),
});

let AddEditServiceGroup = reduxForm(
	{
		form: c.FORM_NAME_ADD_EDIT_SERVICE_GROUP,
		destroyOnUnmount: true,
		enableReinitialize: true,
	},
	mapStateToProps,
)(AddEditServiceGroupForm);

AddEditServiceGroup = connect(mapStateToProps, mapDispatchToProps)(AddEditServiceGroup);

export { AddEditServiceGroup };
